/**
 * Commit ↔ task links over MCP (slice 5): an agent links a commit to a task —
 * "I fixed this in a1b2c3d" — or removes a link it (or a person) made by hand.
 *
 * Links are the task's `commitLinks` (task-manager/taskCommitLinks.js), the
 * same field the app's "Link commit" writes, so a link made here shows in the
 * task window on every device. Automatic links (a key in a commit message or
 * branch) are computed in the app from the local clones; MCP cannot read
 * those clones, so it neither lists nor hides them.
 *
 * Both are appends/removals inside the task record and take no
 * `expected_rev`: like `board_comment_add` they answer concurrency with a
 * bounded CAS retry (read, change, compare-and-swap, three times), so a
 * teammate's edit is never overwritten from a stale read.
 */
import { PERSONAL } from '../scopes';
import { mcpError } from '../errors';
import { scopeReport } from '../writeScope';
import { stageBoardRecords } from '../boardChanges';
import { nowIso, patchedRecord, dataOf, toWireRecord } from '../boardRecords';
import { boardRows, casWrite, findBoard, findTask, revsFor, targetScope } from './boardWriteChecks';
import { commitLinksOf, withCommitLinked } from '../../extensions/bundled/task-manager/taskCommitLinks';

export const LINK_ATTEMPTS = 3;
const SHA = /^[0-9a-f]{7,40}$/i;

/** Read the task, apply `change(taskData)` → next commitLinks (or an error), CAS-write; retried on a lost race. */
async function writeLinks({ boardId, taskId, wire }, change) {
  const target = targetScope(wire);
  if (target.error) return target.error;
  let lastError = null;
  for (let attempt = 0; attempt < LINK_ATTEMPTS; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    const rows = await boardRows(target.scopeKey, boardId);
    const boardRow = findBoard(rows, boardId);
    if (!boardRow) return mcpError('not_found', 'No board with that id in this scope.');
    const taskRow = findTask(rows, taskId);
    if (!taskRow) return mcpError('not_found', 'No task with that id on this board.');
    // eslint-disable-next-line no-await-in-loop
    const revs = await revsFor(target.scopeKey, boardId);
    const rev = revs.get(taskRow.id) ?? null;
    const next = change(dataOf(taskRow));
    if (next.error) return next.error;
    const record = patchedRecord(taskRow, { data: { commitLinks: next.links, updatedAt: nowIso() } });
    // eslint-disable-next-line no-await-in-loop
    const out = await casWrite(target.scopeKey, record, rev);
    if (!out.error) {
      return { boardRow, taskRow, record, rev: out.rev, scopeKey: target.scopeKey, extra: next.extra || {} };
    }
    if (out.error.error !== 'conflict') return out.error;
    lastError = out.error;
  }
  return lastError || mcpError('conflict', 'The task kept changing while linking; read it again.');
}

function shaArg(sha) {
  const s = String(sha ?? '').trim().toLowerCase();
  return SHA.test(s) ? s : null;
}

export async function boardTaskLinkCommit(
  { board_id: boardId, task_id: taskId, sha, summary, repo_name: repoName, branch, author, scope },
  ctx = {}
) {
  const id = shaArg(sha);
  if (!id) return mcpError('invalid_argument', 'sha must be a commit id: 7 to 40 hexadecimal characters.', { field: 'sha' });
  const wire = scope || PERSONAL;
  const agent = ctx?.caller && typeof ctx.caller === 'object' && typeof ctx.caller.agent === 'string' ? ctx.caller.agent : null;
  const out = await writeLinks({ boardId, taskId, wire }, (task) => {
    const links = withCommitLinked(
      task,
      {
        oid: id,
        summary: summary ? String(summary) : '',
        author: author ? String(author).slice(0, 120) : '',
        branch: branch ? String(branch).slice(0, 200) : '',
        repoName: repoName ? String(repoName).slice(0, 120) : '',
      },
      { repo: null, userId: null }
    ).map((l) => (l.sha === id && !l.auto && !l.hidden ? { ...l, linkedVia: agent ? `mcp:${agent}` : 'mcp' } : l));
    return { links };
  });
  if (out.error) return out;
  const result = {
    boardId: out.boardRow.id,
    taskId: out.taskRow.id,
    sha: id,
    linkCount: commitLinksOf(out.record.data).filter((l) => !l.hidden && !l.auto).length,
    scope: wire,
    rev: out.rev,
    ...scopeReport(wire),
  };
  return stageBoardRecords(result, out.scopeKey, [toWireRecord(out.record)]);
}

export async function boardTaskUnlinkCommit({ board_id: boardId, task_id: taskId, sha, scope }) {
  const id = shaArg(sha);
  if (!id) return mcpError('invalid_argument', 'sha must be a commit id: 7 to 40 hexadecimal characters.', { field: 'sha' });
  const wire = scope || PERSONAL;
  const out = await writeLinks({ boardId, taskId, wire }, (task) => {
    const links = commitLinksOf(task);
    // A hand-made link whose id starts with `sha` (a short id is fine). Close
    // markers and hidden automatic links are the app's, never touched here.
    const matches = (l) => !l.auto && !l.hidden && String(l.sha).toLowerCase().startsWith(id);
    const removed = links.filter(matches).length;
    if (!removed) return { error: mcpError('not_found', 'This task has no hand-made link to that commit. Automatic links (the key in a commit message or branch) cannot be removed over MCP.') };
    return { links: links.filter((l) => !matches(l)), extra: { removed } };
  });
  if (out.error) return out;
  const result = {
    boardId: out.boardRow.id,
    taskId: out.taskRow.id,
    sha: id,
    removed: out.extra.removed,
    linkCount: commitLinksOf(out.record.data).filter((l) => !l.hidden && !l.auto).length,
    scope: wire,
    rev: out.rev,
    ...scopeReport(wire),
  };
  return stageBoardRecords(result, out.scopeKey, [toWireRecord(out.record)]);
}
